import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";

interface CTAPath {
  icon?: LucideIcon;
  title: string;
  description: string;
  image?: string;
  cta: { text: string; href: string };
}

interface MultiCTAProps {
  eyebrow?: string;
  headline?: string;
  description?: string;
  paths: CTAPath[];
  variant?: "light" | "dark";
  columns?: 2 | 3;
  compact?: boolean;
}

const MultiCTA = ({
  eyebrow,
  headline,
  description,
  paths,
  variant = "light",
  columns,
  compact = false,
}: MultiCTAProps) => {
  const isDark = variant === "dark";
  const columnClasses = columns === 2
    ? "grid-cols-1 md:grid-cols-2"
    : "grid-cols-1 md:grid-cols-3";

  return (
    <section className={cn(compact ? "py-0" : "py-20 md:py-28", isDark ? "bg-secondary on-dark" : "bg-background")}>
      <div className="mx-auto max-w-wide px-5 md:px-12">
        {/* Header */}
        {(eyebrow || headline || description) && (
          <div className="text-center mb-14 max-w-2xl mx-auto">
            {eyebrow && (
              <p className="font-body text-xs uppercase tracking-[0.2em] mb-3 text-accent">
                {eyebrow}
              </p>
            )}
            {headline && (
              <h2 className="font-headline text-2xl md:text-3xl lg:text-4xl text-foreground">
                {headline}
              </h2>
            )}
            {description && (
              <p className="mt-5 text-muted-foreground text-base md:text-lg leading-relaxed">
                {description}
              </p>
            )}
          </div>
        )}

        {/* Paths */}
        <div className={cn("grid gap-6", columnClasses)}>
          {paths.map((path) => (
            <Link
              key={path.title}
              to={path.cta.href}
              className="group relative overflow-hidden aspect-[4/5] md:aspect-[3/4] flex flex-col justify-end"
            >
              {/* Background image */}
              {path.image && (
                <img
                  src={path.image}
                  alt={path.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                  loading="lazy"
                />
              )}

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-rich-black/90 via-rich-black/40 to-transparent" />

              {/* Glass content card */}
              <div className="relative z-10 m-4 md:m-5 p-5 md:p-6 bg-rich-black/30 backdrop-blur-md border border-cream/10 transition-all duration-500 group-hover:bg-rich-black/40 group-hover:border-cream/20">
              <div className="flex h-full flex-col">
                {path.icon && (
                  <div className="w-10 h-10 flex items-center justify-center bg-accent/15 backdrop-blur-sm border border-accent/20 mb-4">
                    <path.icon className="w-5 h-5 text-accent" strokeWidth={1.5} />
                  </div>
                )}
                <h3 className="font-headline text-xl md:text-2xl text-cream mb-2">
                  {path.title}
                </h3>
                <p className="text-cream/60 text-sm leading-relaxed mb-4">
                  {path.description}
                </p>
                <span className="mt-auto inline-flex items-center gap-2 font-body text-xs uppercase tracking-widest text-accent group-hover:text-accent/90 transition-colors">
                  {path.cta.text}
                  <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
                </span>
              </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MultiCTA;
